define([
    '../Util/defaultValue',
    '../Util/defined'
], function (
    defaultValue,
    defined
) {
    'use strict'

    /**
     * WMTS图层信息类，存储WMTS图层的元数据信息
     * @alias WMTSInformation
     * @constructor
     */
    class WMTSInformation {
        /**
         * @description 图层名称
         * @type {String}
         */
        layerName

        /**
         * @description 样式名称
         * @type {String}
         */
        style

        /**
         * @description 坐标参考系
         * @type {String}
         */
        srs

        /**
         * @description 最小X坐标
         * @type {Number}
         */
        minX

        /**
         * @description 最小Y坐标
         * @type {Number}
         */
        minY

        /**
         * @description 最大X坐标
         * @type {Number}
         */
        maxX

        /**
         * @description 最大Y坐标
         * @type {Number}
         */
        maxY

        /**
         * @description 最小显示层级
         * @type {Number}
         */
        minLevel

        /**
         * @description 最大显示层级
         * @type {Number}
         */
        maxLevel

        /**
         * @description 图片格式
         * @type {String}
         */
        format

        /**
         * @description 协议版本
         * @type {String}
         */
        version

        /**
         * @description 瓦片矩阵集标识符
         * @type {String}
         */
        tileMatrixSet

        /**
         * @description DPI值，无法从xml获取，需手动设置
         * @type {Number}
         */
        dpi

        /**
         * @description 自定义参数，无法从xml获取，需手动设置
         * @type {String}
         */
        customParams

        /**
         * @description 可用的坐标参考系列表
         * @type {Array<String>}
         */
        availableCRS = []

        /**
         * @description 可用的样式列表
         * @type {Array<Object>}
         */
        availableStyles = []

        /**
         * @description 可用的瓦片矩阵集
         * @type {Array<String>}
         */
        availableTileMatrixSets = []

        /**
         * @description 图层标题
         * @type {String}
         */
        title

        /**
         * @description 图层描述
         * @type {String}
         */
        abstract
		
		/**
         * @description 在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "WMTSInformation"
        /**
         * 创建WMTS图层信息对象
         * @param {Object} [props] 配置选项
         */
        constructor(props) {
            if (defined(props)) {
                this.layerName = defaultValue(props.layerName, '')
                this.style = defaultValue(props.style, '')
                this.srs = defaultValue(props.srs, '')
                this.minX = defaultValue(props.minX, 0)
                this.minY = defaultValue(props.minY, 0)
                this.maxX = defaultValue(props.maxX, 0)
                this.maxY = defaultValue(props.maxY, 0)
                this.minLevel = defaultValue(props.minLevel, 0)
                this.maxLevel = defaultValue(props.maxLevel, 16)
                this.format = defaultValue(props.format, 'image/png')
                this.version = defaultValue(props.version, '1.0.0')
                this.tileMatrixSet = defaultValue(props.tileMatrixSet, '')
                this.dpi = defaultValue(props.dpi, 0)
                this.customParams = defaultValue(props.customParams, {})
                this.availableCRS = defaultValue(props.availableCRS, [])
                this.availableStyles = defaultValue(props.availableStyles, [])
                this.availableTileMatrixSets = defaultValue(props.availableTileMatrixSets, [])
                this.title = defaultValue(props.title, '')
                this.abstract = defaultValue(props.abstract, '')
            }
        }

        /**
         * 获取GetTile请求URL模板
         * @param {Object} props 配置选项
         * @param {String} props.url 服务地址
         * @param {String} [props.format] 覆盖默认图片格式
         * @param {String} [props.style] 覆盖默认样式
         * @param {String} [props.tileMatrixSet] 覆盖默认瓦片矩阵集
         * @param {Object} [props.customParams] 覆盖默认自定义参数
         * @returns {String} GetTile请求URL模板，包含{TileMatrix}、{TileRow}、{TileCol}占位符
         */
        getTileUrlTemplate(props) {
            if (!defined(props) || !defined(props.url)) {
                throw new Error('url参数是必需的')
            }

            const format = defaultValue(props.format, this.format)
            const style = defaultValue(props.style, this.style)
            const tileMatrixSet = defaultValue(props.tileMatrixSet, this.tileMatrixSet)
            const customParams = defaultValue(props.customParams, this.customParams)

            let url = props.url
            if (url.indexOf('?') === -1) {
                url += '?'
            } else if (!url.endsWith('?') && !url.endsWith('&')) {
                url += '&'
            }

            let tileUrl = `${url}SERVICE=WMTS&REQUEST=GetTile&VERSION=${this.version}&LAYER=${this.layerName}&STYLE=${style}&TILEMATRIXSET=${tileMatrixSet}&TILEMATRIX={TileMatrix}&TILEROW={TileRow}&TILECOL={TileCol}&FORMAT=${format}`
            
            // 添加自定义参数
            if (customParams) {
                for (const key in customParams) {
                    if (customParams.hasOwnProperty(key)) {
                        tileUrl += `&${key}=${customParams[key]}`
                    }
                }
            }
            
            return tileUrl
        }
        
        /**
         * 设置DPI值
         * @param {Number} dpi DPI值
         */
        setDPI(dpi) {
            this.dpi = dpi
        }
    
        /**
         * 转换为字符串
         * @returns {String} 字符串表示
         */
        toString() {
            return `WMTS Layer: ${this.layerName}, Style: ${this.style}, TileMatrixSet: ${this.tileMatrixSet}`
        }
    }

    return WMTSInformation
})